import React, { useMemo, useState } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import {
  BusinessRole,
  Company,
  PermissionLevel,
} from "fe-shared/src/types/domain";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import useStorage from "fe-shared/src/hooks/useStorage";
import useDialog from "fe-shared/src/hooks/useDialog";
import { Button } from "fe-shared/src/components";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import RequestsDataGrid, {
  getCollectorColumnDefs,
  collectorRenderers,
  getHaulerColumnDefs,
  haulerRenderers,
} from "fe-shared-requests/src/components/RequestsDataGrid";
import { PickupRequestDialog } from "fe-shared-requests/src/components";
import { ParticipantOption } from "fe-shared-requests/src/types";

import { PERMISSIONS, PAGE_SIZE } from "consts";

import getRequestParams from "./modules/config";
import * as api from "./modules/api";

interface Props {
  company: Company;
}

const Requests = ({
  company: {
    permissions,
    businessRoleCode,
    companyId,
    displayName,
    regNumber,
  },
}: Props) => {
  const isHauler = businessRoleCode === BusinessRole.Hauler;
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  // Keyed per role: the two column sets share no filterable field beyond the request number, so a
  // filter kept from the other role's grid would narrow the rows against a column that is not shown.
  const storageKey = isHauler ? "haulerRequests" : "collectorRequests";
  const [options, setOptions] = useStorage<DataGridOptions>(
    `${storageKey}Options`,
    {
      sortDirection: "desc",
      sortField: "submittedDate",
    },
    true
  );
  const [filters, setFilters] = useStorage<DataGridFilters>(
    `${storageKey}Filters`,
    {},
    true
  );

  const requestParams = useMemo(() => getRequestParams(filters, options), [
    filters,
    options,
  ]);

  const requestsQueryKey: QueryKey = ["pickupRequests", requestParams];

  const canViewRequestsList = hasPermission(permissions, {
    name: PERMISSIONS.requestsList,
    level: PermissionLevel.Read,
  });
  const canViewRequest = hasPermission(permissions, {
    name: PERMISSIONS.viewRequest,
    level: PermissionLevel.Read,
  });
  const canAddRequest =
    !isHauler &&
    hasPermission(permissions, {
      name: PERMISSIONS.addRequest,
      level: PermissionLevel.Full,
    });

  const requests = useInfiniteQuery(requestsQueryKey, api.getRequests, {
    enabled: canViewRequestsList,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  const columnDefs = useMemo(
    () => (isHauler ? getHaulerColumnDefs(t) : getCollectorColumnDefs(t)),
    [t, isHauler]
  );

  const { isDialogActive, isDialogRendered, openDialog, closeDialog } =
    useDialog();
  const [dialogInstanceKey, setDialogInstanceKey] = useState(0);

  const lockedCollector: ParticipantOption = {
    companyId,
    displayName,
    regNumber,
  };

  // openDialog() only flips a flag - a close immediately followed by a reopen can leave
  // isDialogRendered continuously true, so the dialog never unmounts and the shared form's
  // validation state, seeded once on mount, carries over the previous values. Bumping this key
  // forces a fresh PickupRequestDialog instance on every opening.
  const onNewRequestButtonClicked = () => {
    setDialogInstanceKey((key) => key + 1);
    openDialog();
  };

  return canViewRequestsList ? (
    <>
      <RequestsDataGrid
        title={t("requests.title")}
        columnDefs={columnDefs}
        frameworkComponents={isHauler ? haulerRenderers : collectorRenderers}
        requests={requests}
        filters={filters}
        options={options}
        setFilters={setFilters}
        setOptions={setOptions}
        lastOpenedRequestId={null}
        clearLastOpenedRequestId={() => {}}
        headerActionsSlot={
          canAddRequest && (
            <Button
              id="open-new-request-dialog-btn"
              onClick={onNewRequestButtonClicked}
              size="medium"
            >
              {t("requests.addNew")}
            </Button>
          )
        }
        onCellClicked={(e) => {
          if (canViewRequest) {
            history.push(`${match.url}/${e.data.requestNumber}`);
          }
        }}
      />
      {isDialogRendered && (
        <PickupRequestDialog
          key={dialogInstanceKey}
          mode="create"
          showPortalOnlyFields={false}
          lockedCollector={lockedCollector}
          requestsQueryKey={requestsQueryKey}
          isActive={isDialogActive}
          onClose={closeDialog}
        />
      )}
    </>
  ) : null;
};

export default Requests;
